import { createContext, type ReactNode, useContext, useEffect } from "react";
import { config } from "@/config";
import { useGlobalPermissions } from "@/domain/usersAndProjects/queryHooks";
import { handleAuthError, isAuthError } from "@/lib/auth/authErrorHandler";
import { useFetch } from "@/lib/react-query/hooks";
import { userQueryKeys } from "@/lib/react-query/keyFactory";
import type { AuthContextType, User } from "./authProvider.types";

const AuthContext = createContext<AuthContextType | null>(null);

interface Props {
  children: ReactNode;
}
const authServiceUrl = config.auth.serviceUrl;
const userUrl = `${authServiceUrl}/api/users/me`;
export function AuthProvider({ children }: Props) {
  const { data, error } = useFetch<{ user: User | null }>({
    url: userUrl,
    queryKey: userQueryKeys.detail("me"),
  });

  const user = data?.user ?? null;

  // Fetch global permissions (only when user is authenticated)
  const { permissions } = useGlobalPermissions({ enabled: !!user });

  useEffect(() => {
    if (error) {
      console.error("Failed to fetch authenticated user:", error);

      // Handle authentication errors (401) by redirecting to login
      // Note: Also treating 500 errors from auth service as auth failures (may need revisiting)
      const shouldRedirectToLogin = isAuthError(error) || error.message?.includes("500");

      if (shouldRedirectToLogin) {
        handleAuthError();
      }
    }
  }, [error]);

  const authContext: AuthContextType = { user, permissions };

  return <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
